import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { buildRedirectUri } from '@/components/brand-connections/utils/oauth';

export default function GoogleDriveCallback() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const didRun = useRef(false);

  const goToConnections = () => {
    sessionStorage.setItem('navigate_to_view', 'connections');
    navigate('/', { replace: true });
  };

  const isUuid = (v: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;

    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const rawState = params.get('state');
      const errorParam = params.get('error');
      const redirectUri = buildRedirectUri('google-drive');

      try { window.history.replaceState({}, document.title, window.location.pathname); } catch { /* noop */ }

      const fail = (message: string) => {
        toast({ title: 'Google Drive Connection Failed', description: message, variant: 'destructive' });
        goToConnections();
      };

      if (errorParam) return fail('Google returned an error during authorization.');
      if (!code || !rawState) return fail('Missing OAuth code or state.');

      const decodedState = (() => { try { return decodeURIComponent(rawState); } catch { return rawState; } })();
      const brandId = decodedState.split(':')[0].replace(/^"+|"+$/g, '').trim();
      if (!isUuid(brandId)) return fail('Invalid brand in OAuth state.');

      const expectedState = sessionStorage.getItem('google_drive_oauth_state');
      if (expectedState && expectedState !== rawState) return fail('OAuth state mismatch. Please try again.');

      try {
        // Send the raw state — the edge function derives the brand from the
        // server-stored state and verifies the same user started the flow.
        const { data, error } = await supabase.functions.invoke('google-drive-oauth', {
          body: { code, state: rawState, redirectUri },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        toast({ title: 'Google Drive Connected ✅', description: 'You can now import files from Google Drive.' });
        goToConnections();
      } catch (err: any) {
        const msg = err?.context?.body?.error || err?.message || 'Failed to complete the Google Drive connection.';
        fail(msg);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center space-y-4 w-full max-w-md px-6">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
        <h2 className="text-2xl font-semibold">Connecting Google Drive…</h2>
        <p className="text-muted-foreground">Please wait while we complete the connection.</p>
      </div>
    </div>
  );
}
